// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectLoading } from 'redux/slices/loadingSlice';

const ACTIVE_TIME_IN_SECONDS = 30;

export const useCheckUserActivity = (params: {
  timeout?: number;
  onDeActive?: () => void;
  condition?: boolean;
  onInactiveInterval?: () => void;
  inactiveIntervalInSeconds?: number;
}) => {
  const {
    timeout = ACTIVE_TIME_IN_SECONDS,
    onDeActive,
    condition = true,
    onInactiveInterval,
    inactiveIntervalInSeconds = 5,
  } = params;
  const { isLoading, offlineInitialized } = useSelector(selectLoading);

  const timeRef = useRef<NodeJS.Timeout | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Store callbacks in refs so timers always call the latest version
  // without causing the effect to re-run on every render
  const onDeActiveRef = useRef(onDeActive);
  const onInactiveIntervalRef = useRef(onInactiveInterval);
  const inactiveIntervalInSecondsRef = useRef(inactiveIntervalInSeconds);
  const timeoutRef = useRef(timeout);
  const conditionRef = useRef(condition);

  useEffect(() => {
    onDeActiveRef.current = onDeActive;
    onInactiveIntervalRef.current = onInactiveInterval;
    inactiveIntervalInSecondsRef.current = inactiveIntervalInSeconds;
    timeoutRef.current = timeout;
    conditionRef.current = condition;
  });

  const stopInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const startInterval = () => {
    if (!onInactiveIntervalRef.current || intervalRef.current) {
      return;
    }

    intervalRef.current = setInterval(() => {
      onInactiveIntervalRef.current?.();
    }, inactiveIntervalInSecondsRef.current * 1000);
  };

  const resetTimer = () => {
    if (!conditionRef.current) {
      return;
    }

    // User is active again — stop the inactive interval
    stopInterval();

    if (timeRef.current) {
      clearTimeout(timeRef.current);
    }

    timeRef.current = setTimeout(() => {
      onDeActiveRef.current?.();
      startInterval();
    }, timeoutRef.current * 1000);
  };

  useEffect(() => {
    if (!condition) {
      return;
    }

    const handleActivity = () => {
      resetTimer();
    };

    const events: (keyof WindowEventMap)[] = [
      'keydown',
      'touchstart',
      'touchmove',
      'click',
      'scroll',
      'wheel',
      'pointerdown',
      'focus',
    ];

    events.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });

    resetTimer();

    return () => {
      if (timeRef.current) {
        clearTimeout(timeRef.current);
      }

      stopInterval();

      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [condition]);

  useEffect(() => {
    if (!condition) {
      return;
    }

    if (!isLoading && offlineInitialized) {
      if (!timeRef.current) {
        resetTimer();
      }
    } else if ((isLoading || !offlineInitialized) && timeRef.current) {
      clearTimeout(timeRef.current);
      timeRef.current = null;
      stopInterval();
    }
  }, [isLoading, offlineInitialized, condition]);
};
